import React, { useEffect, useMemo, useState } from "react";
import { api } from "../utils/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { useToast } from "@/contexts/ToastContext";   // ✅ NEW TOAST

export default function AdditionalInfoBuilderModal({
  applicationId,
  open,
  onClose,
  onCreated,
}) {
  const { showToast } = useToast();   // ✅ USE CUSTOM TOAST

  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [fields, setFields] = useState([
    {
      key: "extra_details",
      label: "",
      type: "textbox",
      options: [],
      required: true,
      description: "",
    },
  ]);

  useEffect(() => {
    if (open) {
      setNote("");
      setFields([
        {
          key: "extra_details",
          label: "",
          type: "textbox",
          options: [],
          required: true,
          description: "",
        },
      ]);
    }
  }, [open]);

  const canSave = useMemo(() => {
    if (!fields.length) return false;
    for (const f of fields) {
      if (!f.key?.trim() || !f.label?.trim() || !f.type) return false;
      if ((f.type === "dropdown" || f.type === "radio") && (!f.options || f.options.length === 0)) {
        return false;
      }
    }
    return true;
  }, [fields]);

  const addField = () => {
    const idx = fields.length + 1;
    setFields((prev) => [
      ...prev,
      {
        key: `field_${idx}`,
        label: `Field ${idx}`,
        type: "textbox",
        options: [],
        required: false,
        description: "",
      },
    ]);
  };

  const removeField = (index) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const updateField = (index, patch) => {
    setFields((prev) =>
      prev.map((f, i) =>
        i === index
          ? {
              ...f,
              ...patch,
              ...(patch.label && (!f.key || f.key.startsWith("field_"))
                ? {
                    key: patch.label
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "_")
                      .replace(/^_|_$/g, "")
                      .slice(0, 40) || f.key,
                  }
                : {}),
            }
          : f
      )
    );
  };

  const onTypeChange = (index, value) => {
    const patch = { type: value };
    if (!(value === "dropdown" || value === "radio")) {
      patch.options = [];
    }
    updateField(index, patch);
  };

  const parseOptions = (csv) => {
    return csv
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const handleCreate = async () => {
    try {
      if (!applicationId) {
        showToast("Missing application ID", "error");  // ✅ NEW
        return;
      }
      if (!canSave) {
        showToast("Please complete all required field details", "error"); // ✅ NEW
        return;
      }

      setSaving(true);

      const payload = {
        note_to_applicant: note,
        fields: fields.map((f) => ({
          key: f.key,
          label: f.label,
          description: f.description || "",
          type: f.type,
          options: (f.type === "dropdown" || f.type === "radio") ? f.options : undefined,
          required: !!f.required,
        })),
      };

      await api.adminCreateAdditionalInfoRound(applicationId, payload);

      showToast("Additional information request created", "success"); // ✅ NEW
      onClose?.();
      onCreated?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to create request";
      showToast(msg, "error"); // ✅ NEW
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50">
      <div className="w-full md:w-[850px] max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl md:rounded-2xl shadow-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Request Additional Information</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 px-2 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Note */}
          {/* <div>
            <Label className="text-sm">Note to applicant (optional)</Label>
            <textarea
              className="mt-2 w-full border rounded px-3 py-2 text-sm"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explain why you need this information or document…"
            />
          </div> */}

          {/* Fields */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Fields to request</h4>
              {/* <Button onClick={addField} className="bg-blue-600 text-white">
                + Add Field
              </Button> */}
            </div>

            {fields.map((f, idx) => (
              <div key={idx} className="border rounded-lg p-4">
                {/* Field form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Label</Label>
                    <input
                      className="mt-2 w-full border rounded px-3 py-2 text-sm"
                      value={f.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      placeholder="e.g., Correct Date of Birth"
                    />
                  </div>

                  <div>
                    <Label className="text-sm">Key (auto)</Label>
                    <input
                      className="mt-2 w-full border rounded px-3 py-2 text-sm"
                      value={f.key}
                      onChange={(e) => updateField(idx, { key: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label className="text-sm">Type</Label>
                    <select
                      className="mt-2 w-full border rounded px-3 py-2 text-sm"
                      value={f.type}
                      onChange={(e) => onTypeChange(idx, e.target.value)}
                    >
                      <option value="textbox">Textbox</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="file">File</option>
                      <option value="date">Date</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="radio">Radio</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm">Required?</Label>
                    <select
                      className="mt-2 w-full border rounded px-3 py-2 text-sm"
                      value={f.required ? "yes" : "no"}
                      onChange={(e) => updateField(idx, { required: e.target.value === "yes" })}
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-sm">Description (optional)</Label>
                    <input
                      className="mt-2 w-full border rounded px-3 py-2 text-sm"
                      value={f.description}
                      onChange={(e) => updateField(idx, { description: e.target.value })}
                    />
                  </div>

                  {(f.type === "dropdown" || f.type === "radio") && (
                    <div className="md:col-span-2">
                      <Label className="text-sm">Options (comma separated)</Label>
                      <input
                        className="mt-2 w-full border rounded px-3 py-2 text-sm"
                        onChange={(e) => updateField(idx, { options: parseOptions(e.target.value) })}
                        defaultValue={f.options?.join(", ")}
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    className="text-red-600 text-sm hover:underline"
                    onClick={() => removeField(idx)}
                    disabled={fields.length === 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="bg-blue-600 text-white"
            onClick={handleCreate}
            disabled={!canSave || saving}
          >
            {saving ? "Creating…" : "Create Request"}
          </Button>
        </div>
      </div>
    </div>
  );
}